import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Button, ButtonVariant } from '@amzn/stencil-react-components/button'
import { Col, Row, View } from '@amzn/stencil-react-components/layout'
import { Spinner } from '@amzn/stencil-react-components/spinner'
import { Text } from '@amzn/stencil-react-components/text'

import { ItemGroupEntity } from 'src/models/dto/activities/ItemGroupDTO'
import { ModuleLayout } from 'src/models/dto/ModuleDTO'
import { LookAndFeelService, LookAndFeelSummary } from 'src/services/backend/LookAndFeelService'
import { ModuleService } from 'src/services/backend/ModuleService'
import { ActivityEntityService } from 'src/services/EntityServices/ActivityEntityService'
import { ItemGroupHandler } from 'src/services/EntityServices/ActivityUpdateHandlers/ItemGroupHandler'
import { ContextBoxEntityService } from 'src/services/EntityServices/ContextBoxEntityService'
import { ModuleEntityService } from 'src/services/EntityServices/ModuleEntityService'

export interface LookAndFeelGalleryProps {
    /** Item type whose look-and-feels to browse (matches the gallery partition key). */
    itemType: string
    /** Entity id of the module currently being edited; the picked page is added to it. */
    moduleEntityId: string
    /** Optional workflow position to insert at; appends to the end when omitted. */
    pageIndex?: number
    /**
     * When set, the picked page's items and context box are merged into this existing page instead
     * of being added as a new page. An existing context box on the target is overwritten (the
     * gallery confirms with the author first).
     */
    targetActivityId?: string
    /** Called after a page has been inserted (e.g. to close the picker). */
    onInserted?: () => void
}

type Variant = 'template' | 'real'

/**
 * A duplicated gallery page awaiting the author's confirmation before it is merged into the target
 * page. `switchLayout` means the module must move to Customer Facing first so the page's context
 * box can take effect; `overwrite` means the target already has an authored context box that the
 * merge would replace. Both can be true at once (e.g. a non-CustomerFacing module whose target
 * page still carries authored context-box content).
 */
type PendingMerge = { dup: ItemGroupEntity; switchLayout: boolean; overwrite: boolean }

const SWITCH_LAYOUT_MESSAGE =
    'This look-and-feel includes a context box, which only works in the Customer Facing layout. ' +
    'To add it, this module must be switched to the Customer Facing layout. Switching will add ' +
    'the context box and items to this page.'

const OVERWRITE_MESSAGE =
    'This page already has a context box, which will be overwritten. Do you want to continue?'

/**
 * Confirmation prompt test ids + button label. Keyed on whether a layout switch is involved: the
 * switch prompt is shown whenever a switch is needed (it may also carry the overwrite message).
 */
const CONFIRM_COPY = {
    'switch-layout': {
        testId: 'customer-facing-switch-confirm',
        cancelTestId: 'cancel-customer-facing-switch',
        confirmTestId: 'confirm-customer-facing-switch',
        confirmLabel: 'Switch to Customer Facing and apply',
    },
    overwrite: {
        testId: 'context-box-overwrite-confirm',
        cancelTestId: 'cancel-context-box-overwrite',
        confirmTestId: 'confirm-context-box-overwrite',
        confirmLabel: 'Overwrite context box',
    },
} as const

const PREVIEW_HEIGHT = 460
const THUMB_WIDTH = 150
const THUMB_HEIGHT = 110
/** Opacity of a disabled nav control. */
const NAV_DISABLED_OPACITY = 0.35

/** Circular translucent nav button pinned to the left/right edge of the preview. */
const navStyle = (side: 'left' | 'right', disabled: boolean): React.CSSProperties => ({
    position: 'absolute',
    top: '50%',
    [side]: 12,
    transform: 'translateY(-50%)',
    zIndex: 5,
    width: 40,
    height: 40,
    borderRadius: '50%',
    border: 'none',
    background: 'rgba(255,255,255,0.9)',
    color: '#16191f',
    fontSize: 24,
    lineHeight: '38px',
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? NAV_DISABLED_OPACITY : 1,
    boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
})

/**
 * A "pick a look-and-feel" gallery. A large preview shows the currently selected look-and-feel; a
 * horizontally scrollable strip of thumbnails lets the author browse and select (template
 * placeholders by default, real renders on toggle). Look-and-feels with no image for the current
 * variant are skipped. An "Apply" button beside the Template/Real toggle fetches the selected
 * scrubbed page and inserts it, reusing the app's own activity hydration + duplication so nested
 * items get fresh ids. By default the page is inserted as a new page; when `targetActivityId` is
 * set the page's items and context box are merged into that existing page instead. Merging a
 * context box first confirms switching the module to the Customer Facing layout when it isn't
 * already (context boxes only render there), and confirms overwriting an authored context box.
 */
export const LookAndFeelGallery = ({
    itemType,
    moduleEntityId,
    pageIndex,
    targetActivityId,
    onInserted,
}: LookAndFeelGalleryProps) => {
    const [items, setItems] = useState<LookAndFeelSummary[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [variant, setVariant] = useState<Variant>('template')
    const [selectedHash, setSelectedHash] = useState<string>()
    const [inserting, setInserting] = useState(false)
    // A duplicated gallery page held back pending the author's confirmation — either because
    // merging it would overwrite an authored context box, or because applying its context box
    // requires switching the module to the Customer Facing layout first.
    const [pending, setPending] = useState<PendingMerge>()
    const selectedThumbRef = useRef<HTMLButtonElement>(null)
    const stripRef = useRef<HTMLDivElement>(null)

    const imageFor = useCallback(
        (it?: LookAndFeelSummary) =>
            it && (variant === 'template' ? it.templateImageUrl : it.realImageUrl),
        [variant]
    )

    // Only show look-and-feels that actually have an image for the current variant; the rest are
    // skipped entirely (no "no image" placeholder).
    const visibleItems = useMemo(() => items.filter((it) => imageFor(it)), [items, imageFor])

    useEffect(() => {
        const abort = new AbortController()
        setLoading(true)
        setError('')
        LookAndFeelService.getItemLookAndFeels(itemType, abort)
            .then((result) => {
                setItems(result)
                const first = result.find((r) => r.templateImageUrl || r.realImageUrl)
                setSelectedHash(first?.signatureHash)
            })
            .catch((e: unknown) => {
                if (!abort.signal.aborted) {
                    console.error('Failed to load look-and-feels', e)
                    setError('Failed to load look-and-feels')
                }
            })
            .finally(() => {
                if (!abort.signal.aborted) {
                    setLoading(false)
                }
            })
        return () => abort.abort()
    }, [itemType])

    // If the current selection isn't visible in this variant, snap to the first visible one.
    useEffect(() => {
        if (visibleItems.length === 0) {
            return
        }
        if (!visibleItems.some((it) => it.signatureHash === selectedHash)) {
            setSelectedHash(visibleItems[0].signatureHash)
        }
    }, [visibleItems, selectedHash])

    const selectedIndex = Math.max(
        0,
        visibleItems.findIndex((it) => it.signatureHash === selectedHash)
    )
    const selected = visibleItems[selectedIndex]
    const selectedHashResolved = selected?.signatureHash
    const selectedUrl = imageFor(selected)

    // Keep the selected thumbnail centered in the (horizontally scrollable) strip as the selection
    // moves, so navigating never pushes it out of view.
    useEffect(() => {
        selectedThumbRef.current?.scrollIntoView?.({
            behavior: 'smooth',
            inline: 'center',
            block: 'nearest',
        })
    }, [selectedHashResolved])

    const move = useCallback(
        (delta: number) => {
            const cur = Math.max(
                0,
                visibleItems.findIndex((it) => it.signatureHash === selectedHash)
            )
            const next = Math.min(visibleItems.length - 1, Math.max(0, cur + delta))
            setSelectedHash(visibleItems[next]?.signatureHash)
        },
        [visibleItems, selectedHash]
    )

    const onKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'ArrowLeft') {
                e.preventDefault()
                move(-1)
            } else if (e.key === 'ArrowRight') {
                e.preventDefault()
                move(1)
            }
        },
        [move]
    )

    // Merge a prepared (duplicated) gallery page's items + context box into the target page, then
    // discard the duplicated wrapper activity (its items and context box now live on the target).
    const mergeInto = useCallback(
        (dup: ItemGroupEntity) => {
            ItemGroupHandler.mergeContent(targetActivityId!, dup.itemIds, dup.contextBoxId)
            ActivityEntityService.remove(dup.id)
            onInserted?.()
        },
        [targetActivityId, onInserted]
    )

    const insert = useCallback(async () => {
        // Defensive: the download button only renders when a look-and-feel is selected.
        /* istanbul ignore next */
        if (!selectedHashResolved) {
            return
        }
        setInserting(true)
        let populatedId: string | undefined
        try {
            const moduleChild = await LookAndFeelService.getItemLookAndFeelPage(
                itemType,
                selectedHashResolved
            )
            // The stored page is the moduleChild; wrap it in an ActivityDTO, hydrate it into the
            // store, then duplicate it so the inserted page and its nested items get fresh ids and
            // the template copy is discarded.
            ModuleService.populateActivities([{ moduleChild }])
            populatedId = moduleChild.id
            const inserted = ActivityEntityService.duplicate(moduleChild.id)

            if (targetActivityId) {
                // Merge mode: fold the picked page's content into the existing target page.
                const dup = inserted as ItemGroupEntity
                const target = ActivityEntityService.get(targetActivityId) as ItemGroupEntity
                const layout = ModuleEntityService.get(moduleEntityId).layout
                // Only warn about overwriting a context box the author has actually filled in.
                // CustomerFacing modules give every page a default (empty) context box, which holds
                // nothing to lose, so overwriting that silently is correct.
                const targetHasAuthoredContextBox =
                    !!target.contextBoxId &&
                    ContextBoxEntityService.hasChangedFromDefault(target.contextBoxId)

                if (
                    dup.contextBoxId &&
                    (layout !== ModuleLayout.CustomerFacing || targetHasAuthoredContextBox)
                ) {
                    // Merging this context box needs the author's confirmation: either to switch
                    // the module to Customer Facing (context boxes only render there) or because it
                    // would overwrite an authored context box — possibly both at once.
                    setPending({
                        dup,
                        switchLayout: layout !== ModuleLayout.CustomerFacing,
                        overwrite: targetHasAuthoredContextBox,
                    })
                } else {
                    mergeInto(dup)
                }
            } else {
                // Default mode: add the picked page as a new page.
                ModuleEntityService.addActivity(moduleEntityId, inserted.id, pageIndex)
                onInserted?.()
            }
        } catch (e: unknown) {
            console.error('Failed to insert look-and-feel page', e)
            setError('Failed to insert the selected look-and-feel')
        } finally {
            // Discard the hydrated template copy on both success and partial failure so it never
            // leaks into the store.
            if (populatedId) {
                ActivityEntityService.remove(populatedId)
            }
            setInserting(false)
        }
    }, [
        selectedHashResolved,
        itemType,
        moduleEntityId,
        pageIndex,
        onInserted,
        targetActivityId,
        mergeInto,
    ])

    const confirmPending = useCallback(() => {
        /* istanbul ignore next */
        if (!pending) {
            return
        }
        if (pending.switchLayout) {
            // Turn on context boxes for the whole module first; the target page's context box is
            // then replaced by the picked page's in mergeInto.
            ModuleEntityService.enableCustomerFacingLayout(moduleEntityId)
        }
        mergeInto(pending.dup)
        setPending(undefined)
    }, [pending, moduleEntityId, mergeInto])

    const cancelPending = useCallback(() => {
        if (pending) {
            // Discard the duplicate we prepared but never merged.
            ActivityEntityService.remove(pending.dup.id)
            setPending(undefined)
        }
    }, [pending])

    if (loading) {
        return (
            <Row padding={24} justifyContent='center'>
                <Spinner />
            </Row>
        )
    }

    if (pending) {
        const copy = CONFIRM_COPY[pending.switchLayout ? 'switch-layout' : 'overwrite']
        return (
            <Col gridGap={16} padding={24} data-test-id={copy.testId}>
                {pending.switchLayout && <Text fontSize='T200'>{SWITCH_LAYOUT_MESSAGE}</Text>}
                {pending.overwrite && <Text fontSize='T200'>{OVERWRITE_MESSAGE}</Text>}
                <Row gridGap={8} justifyContent='flex-end'>
                    <Button
                        variant={ButtonVariant.Secondary}
                        dataTestId={copy.cancelTestId}
                        onClick={cancelPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant={ButtonVariant.Primary}
                        dataTestId={copy.confirmTestId}
                        onClick={confirmPending}
                    >
                        {copy.confirmLabel}
                    </Button>
                </Row>
            </Col>
        )
    }

    return (
        <div style={{ width: 'min(1100px, 88vw)', maxWidth: '100%' }}>
            <Col gridGap={16} width='100%'>
                {error && (
                    <div
                        style={{ color: '#d13212', fontWeight: 600 }}
                        data-test-id='look-and-feel-error'
                    >
                        {error}
                    </div>
                )}

                <Row gridGap={8} alignItems='center'>
                    <View flex={1} />
                    <Button
                        variant={
                            variant === 'template' ? ButtonVariant.Primary : ButtonVariant.Secondary
                        }
                        onClick={() => setVariant('template')}
                    >
                        Template
                    </Button>
                    <Button
                        variant={
                            variant === 'real' ? ButtonVariant.Primary : ButtonVariant.Secondary
                        }
                        onClick={() => setVariant('real')}
                    >
                        Real
                    </Button>
                    {visibleItems.length > 0 && (
                        <>
                            <View width={12} />
                            <Button
                                variant={ButtonVariant.Primary}
                                dataTestId='use-look-and-feel'
                                aria-label='Apply this look-and-feel'
                                disabled={inserting}
                                onClick={() => void insert()}
                            >
                                {inserting ? <Spinner /> : 'Apply'}
                            </Button>
                        </>
                    )}
                </Row>

                {visibleItems.length === 0 ? (
                    <Row
                        height={PREVIEW_HEIGHT}
                        alignItems='center'
                        justifyContent='center'
                        backgroundColor='neutral05'
                    >
                        <Text fontSize='T200' color='neutral70'>
                            {`No ${variant} images captured for this item type yet.`}
                        </Text>
                    </Row>
                ) : (
                    <>
                        {/* Large preview of the selected look-and-feel. */}
                        <div
                            data-test-id='look-and-feel-preview'
                            style={{
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: PREVIEW_HEIGHT,
                                background: '#f4f4f4',
                                borderRadius: 8,
                                padding: 12,
                            }}
                        >
                            <img
                                src={selectedUrl}
                                alt={`look-and-feel ${selectedHashResolved ?? ''}`}
                                style={{
                                    maxHeight: '100%',
                                    maxWidth: '100%',
                                    objectFit: 'contain',
                                }}
                            />

                            {visibleItems.length > 1 && (
                                <>
                                    <button
                                        type='button'
                                        aria-label='Previous look-and-feel'
                                        data-test-id='look-and-feel-prev'
                                        disabled={selectedIndex === 0}
                                        onClick={() => move(-1)}
                                        style={navStyle('left', selectedIndex === 0)}
                                    >
                                        ‹
                                    </button>
                                    <button
                                        type='button'
                                        aria-label='Next look-and-feel'
                                        data-test-id='look-and-feel-next'
                                        disabled={selectedIndex === visibleItems.length - 1}
                                        onClick={() => move(1)}
                                        style={navStyle(
                                            'right',
                                            selectedIndex === visibleItems.length - 1
                                        )}
                                    >
                                        ›
                                    </button>
                                </>
                            )}
                        </div>

                        {/* Horizontally scrollable thumbnail strip. */}
                        <div
                            data-test-id='look-and-feel-strip'
                            ref={stripRef}
                            role='listbox'
                            aria-label='Look and feel thumbnails'
                            tabIndex={0}
                            onKeyDown={onKeyDown}
                            style={{
                                display: 'flex',
                                gap: 12,
                                overflowX: 'auto',
                                padding: '8px 2px',
                                outline: 'none',
                            }}
                        >
                            {visibleItems.map((it) => {
                                const isSelected = it.signatureHash === selectedHashResolved
                                return (
                                    <button
                                        key={it.signatureHash}
                                        ref={isSelected ? selectedThumbRef : undefined}
                                        type='button'
                                        role='option'
                                        aria-selected={isSelected}
                                        aria-label={`look-and-feel ${it.signatureHash}`}
                                        title={it.signatureHash}
                                        data-test-id={`look-and-feel-card-${it.signatureHash}`}
                                        onClick={() => {
                                            setSelectedHash(it.signatureHash)
                                            // Move focus to the strip so the left/right arrow keys
                                            // move the selection, and no focus ring lingers on the
                                            // clicked thumbnail (selection is shown by its border).
                                            stripRef.current?.focus()
                                        }}
                                        style={{
                                            flex: '0 0 auto',
                                            width: THUMB_WIDTH,
                                            height: THUMB_HEIGHT,
                                            padding: 4,
                                            cursor: 'pointer',
                                            background: '#fff',
                                            borderRadius: 8,
                                            border: isSelected
                                                ? '2px solid #0972d3'
                                                : '1px solid #d5dbdb',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <img
                                            src={imageFor(it)}
                                            alt={`look-and-feel ${it.signatureHash}`}
                                            style={{
                                                maxHeight: '100%',
                                                maxWidth: '100%',
                                                objectFit: 'contain',
                                            }}
                                        />
                                    </button>
                                )
                            })}
                        </div>

                        <Row justifyContent='center'>
                            <Text fontSize='T100' color='neutral70'>
                                {`${selectedIndex + 1} of ${visibleItems.length}`}
                                {selectedHashResolved ? ` · ${selectedHashResolved}` : ''}
                            </Text>
                        </Row>
                    </>
                )}
            </Col>
        </div>
    )
}
